import { useSourceLanguage } from "../i18n/LocalizedCopy";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useAuth } from "../context/AuthContext";
import {
  applyWalletCoupon,
  fetchWalletSection,
  fetchWalletSummary,
  normalizeWalletSummary,
  updateWalletPreferences,
} from "./walletApi";

const WalletContext = createContext(null);
const STALE_AFTER_MS = 5 * 60 * 1000;
const SECTION_NAMES = ["transactions", "pending", "coupons", "notifications"];
const LEGACY_SENSITIVE_CACHE_KEYS = [
  "eternua.wallet.v1",
  "eternua.wallet.cooldown.v1",
  "eternua.wallet.pending.cache.v1",
];
const LEGACY_SENSITIVE_CACHE_PREFIXES = [
  "eternua.rewards.space.v2:",
  "eternua.rewards.unlocked.v1:",
];

function accountKeyFor(user) {
  const identity = user?.id ?? user?.username ?? user?.email;
  return identity === undefined || identity === null || identity === ""
    ? null
    : String(identity);
}

function isOfflineNow() {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

function purgeLegacySensitiveCache() {
  if (typeof window === "undefined") return;
  LEGACY_SENSITIVE_CACHE_KEYS.forEach((key) => {
    try {
      window.localStorage.removeItem(key);
      window.sessionStorage.removeItem(key);
    } catch (_error) {
      // Le cache mémoire canonique est déjà vide ; le stockage historique est best effort.
    }
  });
  try {
    Object.keys(window.localStorage)
      .filter((key) =>
        LEGACY_SENSITIVE_CACHE_PREFIXES.some((prefix) => key.startsWith(prefix))
      )
      .forEach((key) => window.localStorage.removeItem(key));
  } catch (_error) {
    // La mémoire du provider reste la seule source côté client.
  }
}

function emptySection() {
  return {
    status: "idle",
    results: [],
    count: 0,
    next: null,
    previous: null,
    error: null,
    lastFetchedAt: null,
  };
}

function emptySections() {
  return SECTION_NAMES.reduce((result, name) => {
    result[name] = emptySection();
    return result;
  }, {});
}

function emptyState(accountKey, status = "idle") {
  return {
    accountKey,
    status,
    data: null,
    error: null,
    lastFetchedAt: null,
    isRefreshing: false,
    isOffline: isOfflineNow(),
    sections: emptySections(),
    couponMutation: { status: "idle", error: null },
    preferenceMutation: { status: "idle", error: null },
  };
}

function publicError(error, fallback) {
  if (error?.status === 401 || error?.status === 403) {
    return new Error("Ta session ne permet plus d’accéder au portefeuille.");
  }
  if (error?.name === "TypeError" || isOfflineNow()) {
    return new Error("Réseau indisponible. Les dernières données connues restent affichées.");
  }
  return error instanceof Error ? error : new Error(fallback);
}

export function WalletProvider({ children }) {
  useSourceLanguage();
  const { user, loading: authLoading } = useAuth();
  const accountKey = useMemo(() => accountKeyFor(user), [user]);
  const [state, setState] = useState(() => emptyState(null));
  const [amountsHidden, setAmountsHidden] = useState(false);
  const [showHeaderBalance, setShowHeaderBalance] = useState(true);
  const activeAccountRef = useRef(null);
  const generationRef = useRef(0);
  const summaryInFlightRef = useRef(null);
  const sectionInFlightRef = useRef(new Map());
  const latestSectionRequestRef = useRef(new Map());
  const sectionRequestSequenceRef = useRef(0);

  const invalidateInFlightRequests = useCallback(() => {
    generationRef.current += 1;
    summaryInFlightRef.current = null;
    sectionInFlightRef.current = new Map();
    latestSectionRequestRef.current = new Map();
  }, []);

  const refresh = useCallback(() => {
    const requestAccount = accountKey;
    if (!requestAccount || authLoading) return Promise.resolve(null);

    const currentInFlight = summaryInFlightRef.current;
    if (currentInFlight?.accountKey === requestAccount) {
      return currentInFlight.promise;
    }

    const generation = generationRef.current;
    const offline = isOfflineNow();
    setState((current) => {
      const sameAccount = current.accountKey === requestAccount;
      const hasData = sameAccount && Boolean(current.data);
      return {
        ...(sameAccount ? current : emptyState(requestAccount)),
        accountKey: requestAccount,
        status: offline ? "offline" : hasData ? current.status : "loading",
        error: offline && !hasData
          ? new Error("Réseau indisponible. Reconnecte-toi pour charger ton portefeuille.")
          : hasData
            ? current.error
            : null,
        isRefreshing: !offline,
        isOffline: offline,
      };
    });

    if (offline) return Promise.resolve(null);

    const promise = fetchWalletSummary()
      .then((data) => {
        if (
          activeAccountRef.current !== requestAccount ||
          generationRef.current !== generation
        ) {
          return null;
        }
        setState((current) => ({
          ...current,
          accountKey: requestAccount,
          status: "ready",
          data,
          error: null,
          lastFetchedAt: new Date().toISOString(),
          isRefreshing: false,
          isOffline: false,
        }));
        setAmountsHidden(data.preferences?.hideBalances === true);
        setShowHeaderBalance(data.preferences?.showHeaderBalance !== false);
        return data;
      })
      .catch((error) => {
        if (
          activeAccountRef.current !== requestAccount ||
          generationRef.current !== generation ||
          error?.name === "AbortError"
        ) {
          return null;
        }
        const normalizedError = publicError(error, "Le portefeuille est momentanément indisponible.");
        setState((current) => {
          const hasData = current.accountKey === requestAccount && Boolean(current.data);
          const offlineFailure = isOfflineNow() || error?.name === "TypeError";
          return {
            ...current,
            status: offlineFailure ? "offline" : hasData ? "stale" : "error",
            error: normalizedError,
            isRefreshing: false,
            isOffline: offlineFailure,
          };
        });
        return null;
      })
      .finally(() => {
        if (summaryInFlightRef.current?.promise === promise) {
          summaryInFlightRef.current = null;
        }
      });

    summaryInFlightRef.current = { accountKey: requestAccount, promise };
    return promise;
  }, [accountKey, authLoading]);

  const reconcileSummary = useCallback((payload) => {
    let summary;
    try {
      summary = normalizeWalletSummary(payload);
    } catch (_error) {
      return false;
    }
    setAmountsHidden(summary.preferences?.hideBalances === true);
    setShowHeaderBalance(summary.preferences?.showHeaderBalance !== false);
    setState((current) => {
      if (current.accountKey !== accountKey) return current;
      return {
        ...current,
        status: "ready",
        data: summary,
        error: null,
        lastFetchedAt: summary.updatedAt || new Date().toISOString(),
        isOffline: false,
      };
    });
    return true;
  }, [accountKey]);

  const loadSection = useCallback(
    (section, { page = 1, force = false } = {}) => {
      if (!SECTION_NAMES.includes(section)) {
        return Promise.reject(new Error(`Section portefeuille inconnue : ${section}`));
      }
      const requestAccount = accountKey;
      if (!requestAccount || authLoading) return Promise.resolve(null);
      const normalizedPage = Math.max(1, Number(page) || 1);
      const requestKey = `${requestAccount}:${section}:${normalizedPage}`;
      const existing = sectionInFlightRef.current.get(requestKey);
      if (existing && !force) return existing;

      const generation = generationRef.current;
      const requestToken = {
        key: requestKey,
        sequence: ++sectionRequestSequenceRef.current,
      };
      latestSectionRequestRef.current.set(section, requestToken);
      const offline = isOfflineNow();
      setState((current) => {
        if (current.accountKey !== requestAccount) return current;
        const previous = current.sections[section] || emptySection();
        return {
          ...current,
          sections: {
            ...current.sections,
            [section]: {
              ...previous,
              status: offline
                ? "offline"
                : previous.results.length > 0
                  ? "stale"
                  : "loading",
              error: offline
                ? new Error("Réseau indisponible.")
                : null,
            },
          },
        };
      });
      if (offline) return Promise.resolve(null);

      const promise = fetchWalletSection(section, { page: normalizedPage })
        .then((payload) => {
          if (
            activeAccountRef.current !== requestAccount ||
            generationRef.current !== generation ||
            latestSectionRequestRef.current.get(section) !== requestToken
          ) {
            return null;
          }
          setState((current) => ({
            ...current,
            sections: {
              ...current.sections,
              [section]: {
                status: "ready",
                ...payload,
                error: null,
                lastFetchedAt: new Date().toISOString(),
              },
            },
          }));
          return payload;
        })
        .catch((error) => {
          if (
            activeAccountRef.current !== requestAccount ||
            generationRef.current !== generation ||
            latestSectionRequestRef.current.get(section) !== requestToken ||
            error?.name === "AbortError"
          ) {
            return null;
          }
          const normalizedError = publicError(error, "Cette section est momentanément indisponible.");
          setState((current) => {
            const previous = current.sections[section] || emptySection();
            return {
              ...current,
              sections: {
                ...current.sections,
                [section]: {
                  ...previous,
                  status: previous.results.length > 0 ? "stale" : isOfflineNow() ? "offline" : "error",
                  error: normalizedError,
                },
              },
            };
          });
          return null;
        })
        .finally(() => {
          if (sectionInFlightRef.current.get(requestKey) === promise) {
            sectionInFlightRef.current.delete(requestKey);
          }
        });

      sectionInFlightRef.current.set(requestKey, promise);
      return promise;
    },
    [accountKey, authLoading]
  );

  const submitCoupon = useCallback(
    async (code) => {
      const requestAccount = accountKey;
      if (!requestAccount || authLoading) {
        throw new Error("Connecte-toi pour utiliser un coupon.");
      }
      const currentCapabilities =
        state.accountKey === requestAccount ? state.data?.capabilities : null;
      if (!currentCapabilities?.couponsEnabled) {
        throw new Error("Cette fonctionnalité sera disponible au lancement.");
      }

      setState((current) => ({
        ...current,
        couponMutation: { status: "loading", error: null },
      }));
      try {
        const result = await applyWalletCoupon(code);
        // La mutation appartient au compte qui l'a déclenchée. Si l'utilisateur
        // a changé entre-temps, sa réponse ne doit invalider ni réconcilier B.
        if (activeAccountRef.current !== requestAccount) return result;
        // A GET lancé avant le POST ne doit jamais écraser le résumé officiel
        // renvoyé par la mutation. La nouvelle génération invalide aussi les
        // anciennes sections avant leur revalidation forcée.
        invalidateInFlightRequests();
        if (result?.summary) reconcileSummary(result.summary);
        setState((current) => ({
          ...current,
          couponMutation: { status: "success", error: null },
        }));
        await Promise.all([
          refresh(),
          loadSection("coupons", { force: true }),
          loadSection("transactions", { force: true }),
          loadSection("notifications", { force: true }),
        ]);
        return result;
      } catch (error) {
        const normalizedError = publicError(error, "Le coupon n’a pas pu être vérifié.");
        if (activeAccountRef.current === requestAccount) {
          setState((current) => ({
            ...current,
            couponMutation: { status: "error", error: normalizedError },
          }));
        }
        throw normalizedError;
      }
    },
    [
      accountKey,
      authLoading,
      invalidateInFlightRequests,
      loadSection,
      reconcileSummary,
      refresh,
      state.accountKey,
      state.data,
    ]
  );

  const savePreferences = useCallback(
    async (nextPreferences) => {
      const requestAccount = accountKey;
      if (!requestAccount || authLoading) {
        throw new Error("Connecte-toi pour modifier les préférences du portefeuille.");
      }
      setState((current) => ({
        ...current,
        preferenceMutation: { status: "loading", error: null },
      }));
      try {
        const preferences = await updateWalletPreferences(nextPreferences);
        if (activeAccountRef.current !== requestAccount) return preferences;
        // Le PATCH est la source officielle : toute lecture commencée avant lui
        // doit être rendue inoffensive avant d'appliquer puis de revalider.
        invalidateInFlightRequests();
        setAmountsHidden(preferences.hideBalances);
        setShowHeaderBalance(preferences.showHeaderBalance);
        setState((current) => ({
          ...current,
          data: current.data
            ? { ...current.data, preferences }
            : current.data,
          preferenceMutation: { status: "success", error: null },
        }));
        void refresh();
        return preferences;
      } catch (error) {
        const normalizedError = publicError(error, "Les préférences n’ont pas pu être enregistrées.");
        if (activeAccountRef.current === requestAccount) {
          setState((current) => ({
            ...current,
            preferenceMutation: { status: "error", error: normalizedError },
          }));
        }
        throw normalizedError;
      }
    },
    [accountKey, authLoading, invalidateInFlightRequests, refresh]
  );

  const toggleAmountsHidden = useCallback(
    () => savePreferences({ hideBalances: !amountsHidden }),
    [amountsHidden, savePreferences]
  );
  const toggleHeaderBalance = useCallback(
    () => savePreferences({ showHeaderBalance: !showHeaderBalance }),
    [savePreferences, showHeaderBalance]
  );

  useEffect(() => {
    const accountChanged = activeAccountRef.current !== accountKey;
    if (accountChanged) {
      generationRef.current += 1;
      activeAccountRef.current = accountKey;
      summaryInFlightRef.current = null;
      sectionInFlightRef.current = new Map();
      latestSectionRequestRef.current = new Map();
      purgeLegacySensitiveCache();
      setAmountsHidden(false);
      setShowHeaderBalance(true);
      setState(
        emptyState(
          accountKey,
          accountKey && !authLoading ? (isOfflineNow() ? "offline" : "loading") : "idle"
        )
      );
    }
    if (accountKey && !authLoading) void refresh();
  }, [accountKey, authLoading, refresh]);

  useEffect(() => {
    if (!accountKey || authLoading || typeof window === "undefined") return undefined;

    const onFocus = () => void refresh();
    const onVisibility = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    const onOnline = () => void refresh();
    const onOffline = () => {
      setState((current) => {
        if (current.accountKey !== accountKey) return current;
        return {
          ...current,
          status: "offline",
          isOffline: true,
          isRefreshing: false,
          error: new Error("Réseau indisponible. Les données ne peuvent pas être actualisées."),
        };
      });
    };
    const onWalletInvalidated = (event) => {
      if (event?.detail?.source === "welcome-offer") return;
      const serverSummary = event?.detail?.wallet_summary;
      invalidateInFlightRequests();
      if (serverSummary) reconcileSummary(serverSummary);
      void refresh();
      void loadSection("transactions", { force: true });
    };
    const onWalletNotificationsInvalidated = () => {
      void loadSection("notifications", { force: true });
    };
    const onWelcomeOfferWalletInvalidated = (event) => {
      const serverSummary = event?.detail?.wallet_summary;
      invalidateInFlightRequests();
      if (serverSummary) reconcileSummary(serverSummary);
      void refresh();
      void loadSection("transactions", { force: true });
      void loadSection("notifications", { force: true });
    };

    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener(
      "eternua:wallet-refresh-requested",
      onWelcomeOfferWalletInvalidated
    );
    window.addEventListener("eternua:wallet-updated", onWalletInvalidated);
    window.addEventListener("eternua:rewards-refresh-requested", onWalletInvalidated);
    window.addEventListener("eternua:rewards-updated", onWalletInvalidated);
    window.addEventListener(
      "eternua:notifications-refresh-requested",
      onWalletNotificationsInvalidated
    );
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener(
        "eternua:wallet-refresh-requested",
        onWelcomeOfferWalletInvalidated
      );
      window.removeEventListener("eternua:wallet-updated", onWalletInvalidated);
      window.removeEventListener("eternua:rewards-refresh-requested", onWalletInvalidated);
      window.removeEventListener("eternua:rewards-updated", onWalletInvalidated);
      window.removeEventListener(
        "eternua:notifications-refresh-requested",
        onWalletNotificationsInvalidated
      );
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [
    accountKey,
    authLoading,
    invalidateInFlightRequests,
    loadSection,
    reconcileSummary,
    refresh,
  ]);

  useEffect(() => {
    if (state.status !== "ready" || !state.lastFetchedAt) return undefined;
    const elapsed = Date.now() - new Date(state.lastFetchedAt).getTime();
    const delay = Math.max(0, STALE_AFTER_MS - elapsed);
    const timeout = window.setTimeout(() => {
      setState((current) =>
        current.accountKey === accountKey && current.status === "ready"
          ? { ...current, status: "stale" }
          : current
      );
    }, delay);
    return () => window.clearTimeout(timeout);
  }, [accountKey, state.lastFetchedAt, state.status]);

  const sameAccount = state.accountKey === accountKey;
  const publicState = sameAccount
    ? state
    : emptyState(accountKey, accountKey && !authLoading ? "loading" : "idle");

  const value = useMemo(
    () => ({
      ...publicState,
      isAuthenticated: Boolean(accountKey),
      amountsHidden,
      showHeaderBalance,
      toggleAmountsHidden,
      toggleHeaderBalance,
      savePreferences,
      refresh,
      loadSection,
      submitCoupon,
    }),
    [
      accountKey,
      amountsHidden,
      loadSection,
      publicState,
      refresh,
      savePreferences,
      showHeaderBalance,
      submitCoupon,
      toggleAmountsHidden,
      toggleHeaderBalance,
    ]
  );

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}

export function useWallet() {
  const value = useContext(WalletContext);
  if (!value) throw new Error("useWallet doit être utilisé dans WalletProvider.");
  return value;
}

export function useWalletSummary() {
  const {
    status,
    data,
    error,
    lastFetchedAt,
    isRefreshing,
    isOffline,
    refresh,
    amountsHidden,
    showHeaderBalance,
  } = useWallet();
  return {
    status,
    data,
    error,
    lastFetchedAt,
    isRefreshing,
    isOffline,
    refresh,
    amountsHidden,
    showHeaderBalance,
  };
}

export function useWalletSection(section) {
  const { sections, loadSection } = useWallet();
  const load = useCallback((options) => loadSection(section, options), [loadSection, section]);
  return {
    ...(sections[section] || emptySection()),
    load,
  };
}
